"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function TheBornBeautifulExperience() {
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: watermarkRef,
    isVisible: isWatermarkVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="252398" id="a-note-of-welcome" className="text-foreground py-24 lg:py-48 overflow-hidden relative bg-muted">
      
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/natural-paper.png')]" />

      <div className="container mx-auto px-6 md:px-12 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24 items-center">
          
          <div ref={watermarkRef} className={`lg:col-span-6 relative transition-all duration-1000 ease-out ${isWatermarkVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="relative aspect-[4/5] w-full max-w-md mx-auto lg:mx-0">
              <div className="absolute -inset-4 border border-accent rounded-lg translate-x-2 translate-y-2" aria-hidden="true" />
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678925/img/wedding_arc_seats_only.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678925/img/wedding_arc_seats_only.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678925/img/wedding_arc_seats_only.png-wvc-sizes" alt="Wedding Decoration" className="w-full h-full object-cover rounded-lg shadow-xl relative z-10" loading="lazy" decoding="async" />
              <div className="absolute -bottom-34 -left-12 hidden lg:block select-none pointer-events-none">
                <span className="text-[10vw] font-serif text-primary/5 leading-none uppercase tracking-[0.2em] [writing-mode:vertical-rl] rotate-180">
                  Welcome
                </span>
              </div>
            </div>
          </div>

          <div ref={contentRef} className={`lg:col-span-6 transition-all duration-1000 delay-300 ease-out ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="max-w-xl">
              <div className="mb-4">
                <span className="text-[10px] font-sans font-medium tracking-[0.3em] text-secondary-foreground/60 uppercase">
                  Our Journey Together
                </span>
              </div>
              
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground mb-8 leading-[1.1] italic font-light">
                A Note of Welcome
              </h2>
              
              <div className="space-y-8 text-foreground/80 font-sans text-lg leading-relaxed tracking-wide">
                <p>
                  We are so incredibly honored to have you join us as we begin our greatest adventure yet. Your presence in our lives has shaped who we are today, and we couldn't imagine this day without you.
                </p>
                <p>
                  As we prepare to exchange our vows, we wanted to create a space where we could share our story and all the details of our celebration with the people we love most. We cannot wait to celebrate this day with you.
                </p>
              </div>

              <div className="mt-12 flex flex-wrap gap-6 items-center">
                <Link to="/OurStoryPage">
                  <Button variant="outline" size="lg" className="border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground rounded-lg px-10 py-7 text-[11px] tracking-[0.2em] uppercase transition-all duration-500 font-sans shadow-sm">
                    Read Our Story
                  </Button>
                </Link>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}